import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="update-license-price"
//
// Prices the backoffice offer form. The figures are not kept here: the form hands
// over PackageConcern::PACKAGE_PRICES, so the backoffice quotes what the wizard
// and the API quote, and a price change stays a one-place change.
export default class extends Controller {
  static targets = [
    "maxMembersInput",
    "countryCodeInput",
    "licencePriceInput",
    "moneyInput",
    "subTotalInput",
    "discountInput",
    "priceInput",
    "invoiceType",
  ];

  static values = {
    // PackageConcern::PACKAGE_PRICES: country code (or "default") -> package ->
    // item type -> yearly price. A product missing from a country's table is not
    // sold there; a country missing from the table is priced from "default".
    prices: Object,
    // PackageConcern::PACKAGE_MEMBER_LIMITS: package -> largest member count it
    // serves. package_custom has none and is the fallback above the largest one.
    memberLimits: Object,
    // Currency of each price table, keyed like `prices`.
    currencies: Object,
    // Only a new offer is prefilled. An existing one keeps the prices it was
    // quoted at until someone changes its country or member count by hand.
    prefillOnConnect: Boolean,
  };

  connect() {
    // Only a prefill recalculates on load. The totals a form arrives with are
    // server-rendered - the group invoice form's are gross, which this
    // controller does not compute - so recomputing them here would be wrong.
    if (this.prefillOnConnectValue) {
      this.updatePrice();
    } else {
      this.applyCurrency();
      this.requirePricesOfChosenItems();
    }
    if (this.hasInvoiceTypeTarget) {
      this.toggleInvoiceData();
    }
  }

  updatePrice() {
    const prices = this.pricesForPackage();

    // Empty when the table has no figure: package_custom carries no price row,
    // and a country may not list an item (CH has no standalone tools/courses).
    // Both stay offerable - sales types the price by hand, and
    // requirePricesOfChosenItems keeps the form from leaving without one.
    this.licencePriceInputTargets.forEach((input) => {
      input.value = prices[input.dataset.itemType] ?? "";
    });

    this.applyCurrency();
    this.recalculatePrice();
  }

  recalculatePrice() {
    this.requirePricesOfChosenItems();

    let price = 0;
    let discount = 0;

    this.element
      .querySelectorAll('[data-type="offerable_item"]')
      .forEach((input) => {
        // A licence or workshop counts once its box is ticked; the free-text
        // item on the group invoice form counts once it is named.
        const isCheckbox = input.type === "checkbox";
        if (isCheckbox ? !input.checked : !input.value) return;

        const idPart = isCheckbox ? "offerable_item_id" : "title";
        price += this.amountFor(input, idPart, "price");
        discount += this.amountFor(input, idPart, "discount");
      });

    this.discountInputTarget.value = discount || 0;
    this.subTotalInputTarget.value = price || 0;
    this.priceInputTarget.value = price - discount || 0;
  }

  toggleInvoiceData() {
    if (this.invoiceTypeTarget.value === "pdf_xml") {
      document.querySelector(".xml-invoice-fields").classList.remove("d-none");
      const xmlEmailField = document.querySelector(
        "#group_invoice_form_invoice_xml_email"
      );
      const invoiceEmailField = document.querySelector(
        "#group_invoice_form_invoice_email"
      );
      if (xmlEmailField && invoiceEmailField && !xmlEmailField.value) {
        xmlEmailField.value = invoiceEmailField.value;
      }
    } else {
      document.querySelector(".xml-invoice-fields").classList.add("d-none");
    }
  }

  // The country the form is currently on, as a key into `prices` and
  // `currencies` - "default" for one without its own table.
  priceTableKey() {
    const countryCode = this.hasCountryCodeInputTarget
      ? this.countryCodeInputTarget.value.toUpperCase()
      : "";

    return countryCode in this.pricesValue ? countryCode : "default";
  }

  pricesForPackage() {
    const table = this.pricesValue[this.priceTableKey()] || {};

    return table[this.packageForMaxMembers()] || {};
  }

  // A ticked licence must carry a price; mirrors CompanyOffer#check_items_price
  // so the browser stops the submit before the server has to.
  requirePricesOfChosenItems() {
    this.licencePriceInputTargets.forEach((input) => {
      input.required = Boolean(this.checkboxFor(input)?.checked);
    });
  }

  // Mirrors PackageConcern.package_for_max_members: the first tier whose limit
  // covers the count, and package_custom for 0 (no limit) or anything above the
  // largest tier.
  packageForMaxMembers() {
    const maxMembersCount = Number(this.maxMembersInputTarget.value);
    if (!maxMembersCount) return "package_custom";

    const tier = Object.entries(this.memberLimitsValue).find(
      ([, limit]) => limit === null || maxMembersCount <= limit
    );

    return tier ? tier[0] : "package_custom";
  }

  applyCurrency() {
    const currency = this.currenciesValue[this.priceTableKey()];
    if (!currency) return;

    this.moneyInputTargets.forEach((input) => {
      const suffix = input
        .closest(".input-group")
        ?.querySelector(".input-group-text");
      if (suffix) suffix.textContent = currency;
    });
  }

  checkboxFor(priceInput) {
    return document.getElementById(
      priceInput.id.replace(/_price$/, "_offerable_item_id")
    );
  }

  amountFor(input, idPart, field) {
    const amountInput = document.getElementById(
      input.id.replace(idPart, field)
    );

    return parseFloat(amountInput?.value || 0) || 0;
  }
}
